import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { BoardResponse } from '../services/api/board';
import { archiveTask } from '../services/api/board';
import { applyOptimisticDelete } from './useDeleteTask';

export interface ArchiveTaskInput {
  projectId: string;
  taskId: number;
}

interface MutationContext {
  previous: BoardResponse | undefined;
}

/**
 * Quick task 260825-ik4 (D-1) — soft archive. Optimistically removes the
 * task row from the board cache exactly like `useDeleteTask` (an archived
 * task leaves the board the same way a deleted one does — reuses
 * `applyOptimisticDelete` rather than a second board-pruning helper); rolls
 * back on error. Server audit row is written by the backend after the write.
 *
 * Modal-close side-effect is the caller's responsibility — pass a per-mutate
 * { onSuccess: () => closeModal() } option from the caller.
 */
export function useArchiveTask() {
  const queryClient = useQueryClient();

  return useMutation<{ archived: boolean; taskId: number }, Error, ArchiveTaskInput, MutationContext>({
    mutationFn: (input) => archiveTask(input.taskId),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: ['board', input.projectId, 'internal'] });
      const previous = queryClient.getQueryData<BoardResponse>([
        'board',
        input.projectId,
        'internal',
      ]);
      if (previous) {
        queryClient.setQueryData<BoardResponse>(
          ['board', input.projectId, 'internal'],
          applyOptimisticDelete(previous, input.taskId),
        );
      }
      return { previous };
    },
    onError: (_err, vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(['board', vars.projectId, 'internal'], context.previous);
      }
    },
    onSettled: (_data, _err, input) => {
      queryClient.invalidateQueries({
        queryKey: ['board', input.projectId, 'internal'],
      });
    },
  });
}
